/**
 * API Interceptor for consistent client_id handling
 */
import { getClientIdFromUrl } from './clientId';

// Tracking variables to avoid duplicate error logs
const loggedRequestErrors = new Set();
const loggedResponseErrors = new Set();

/**
 * Set up axios request interceptor to automatically add client_id
 * to all API requests if not already present
 * 
 * @param {object} apiClient - The axios instance to add interceptors to
 */
export const setupClientIdInterceptor = (apiClient) => {
  // Add request interceptor
  apiClient.interceptors.request.use(config => {
    // Skip adding client_id for database API endpoints
    if (config.url && config.url.includes('/api/v1/db/')) {
      // If client_id was accidentally added to params for a db endpoint, remove it
      if (config.params && config.params.client_id) {
        const { client_id, ...paramsWithoutClientId } = config.params;
        config.params = paramsWithoutClientId;
      }
      return config;
    }

    // For non-db endpoints, add client_id if not present
    const urlClientId = getClientIdFromUrl();
    const clientId = urlClientId || localStorage.getItem('selected_client_id') || 'default';
    
    // Add client_id to query params
    if (!config.params) {
      config.params = {};
    }
    
    if (!config.params.client_id) {
      config.params.client_id = clientId;
    }
    
    return config;
  }, error => {
    // Only log request errors that haven't been logged yet
    const errorKey = `${error.config?.url || 'unknown'}-request`;
    if (!loggedRequestErrors.has(errorKey)) {
      loggedRequestErrors.add(errorKey);
      console.error('Error in request interceptor:', error);
    }
    return Promise.reject(error);
  });
  
  // Add response error interceptor
  apiClient.interceptors.response.use(response => {
    return response;
  }, error => {
    // Skip database API endpoints in error logging
    if (error.config?.url?.includes('/api/v1/db/')) {
      // For 404 errors on DB endpoints, we track them in the missing endpoints system
      // But don't need to log them in the console
      return Promise.reject(error);
    }
    
    const errorKey = `${error.config?.url || 'unknown'}-${error.response?.status || 'unknown'}`;
    
    // For 404 errors, use warning instead of error
    if (error.response?.status === 404) {
      if (!loggedResponseErrors.has(errorKey)) {
        loggedResponseErrors.add(errorKey);
        console.warn(`Resource not found: ${error.config?.url}`);
      }
    }
    // For all other errors, log only once
    else if (!loggedResponseErrors.has(errorKey)) {
      loggedResponseErrors.add(errorKey);
      console.error('API error:', error);
    }
    
    return Promise.reject(error);
  });
}; 

/**
 * Set up axios request interceptor to automatically add Authorization header
 * with the AuthGuard access token from localStorage to all requests.
 *
 * @param {object} apiClient - The axios instance to add interceptors to
 */
export const setupAuthGuardTokenInterceptor = (apiClient) => {
  apiClient.interceptors.request.use(config => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authguard_access_token') : null;
      if (token) {
        // Do not override if already set explicitly
        if (!config.headers) config.headers = {};
        if (!config.headers['Authorization'] && !config.headers['authorization']) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch (e) {
      // Non-fatal: proceed without auth header
      console.warn('Unable to read auth token from localStorage:', e);
    }
    return config;
  }, error => Promise.reject(error));
};